import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    CorporateEnrollmentService,
    CoverageType,
    CorporateEnrollmentParticipant,
    CompanyPaymentPlan,
    AddParticipantsRequest
} from '../../../services/corporate-enrollment.service';
import { ParticipantService } from '../../../services/participant.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { ConfirmationConfig } from '../../shared/confirmation-modal/confirmation-modal.component';

interface ParticipantEntry {
    participantId: number | null;
    coverageType: string;
    coverageValue: number | null;
}

@Component({
    selector: 'app-add-corporate-participants',
    templateUrl: './add-corporate-participants.component.html',
    styleUrls: ['./add-corporate-participants.component.scss']
})
export class AddCorporateParticipantsComponent implements OnInit {
    agreementId!: number;
    enrollmentDetails: any = null;
    isLoading = false;
    step = 1;

    // Participantes a agregar
    businessParticipants: ParticipantEntry[] = [];
    participants: any[] = [];

    // Cobertura
    CoverageType = CoverageType;
    Math = Math; // Para usar en template
    inscriptionAmount = 0;
    currencySymbol = '$';

    // Planes de pago de empresa
    companyPaymentPlans: CompanyPaymentPlan[] = [];
    existingCompanyPlans: any[] = [];
    masterPaymentPlans: any[] = []; // Plan maestro existente (solo lectura)
    totalCompanyAmount = 0;
    existingCompanyTotal = 0; // Total original de empresa

    // Cálculos
    totalNewParticipantsAmount = 0;
    totalParticipantPayAmount = 0;

    // Modal de confirmación
    showConfirmationModal = false;
    confirmationConfig: ConfirmationConfig = {
        title: 'Confirmar',
        message: '',
        confirmText: 'Sí, agregar',
        cancelText: 'Cancelar',
        type: 'info'
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private corporateService: CorporateEnrollmentService,
        private participantService: ParticipantService,
        private toast: ToastService,
        private loading: LoadingService
    ) { }

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('agreementId');
        if (id) {
            this.agreementId = +id;
            this.loadEnrollmentDetails();
        } else {
            this.toast.showError('ID de inscripción no proporcionado');
            this.router.navigate(['/dashboard/enrollments/corporate']);
        }
    }

    loadEnrollmentDetails(): void {
        this.loading.show();
        this.corporateService.getAgreementDetails(this.agreementId).subscribe({
            next: (response: any) => {
                this.loading.hide();
                if (response.isSuccess && response.data) {
                    this.enrollmentDetails = response.data;
                    this.setupFromEnrollmentDetails();
                    this.loadParticipants();
                } else {
                    this.toast.showError('No se pudo cargar la inscripción corporativa');
                    this.router.navigate(['/dashboard/enrollments/corporate']);
                }
            },
            error: (err) => {
                this.loading.hide();
                this.toast.showError('Error al cargar la inscripción: ' + err.message);
                this.router.navigate(['/dashboard/enrollments/corporate']);
            }
        });
    }

    setupFromEnrollmentDetails(): void {
        // Obtener monto de inscripción del proyecto
        this.inscriptionAmount = this.enrollmentDetails.project?.inscriptionAmountCOP || 0;

        // Verificar si es internacional
        if (this.enrollmentDetails.isInternational) {
            this.inscriptionAmount = this.enrollmentDetails.project?.inscriptionAmountUSD || this.inscriptionAmount;
            this.currencySymbol = this.enrollmentDetails.currencySymbol || '$';
        }

        // Cargar planes de empresa existentes
        this.existingCompanyPlans = this.enrollmentDetails.companyPaymentPlans || [];

        // Cargar plan maestro existente (solo lectura para información)
        this.masterPaymentPlans = this.enrollmentDetails.masterPaymentPlans || [];

        // Calcular total original de empresa
        this.existingCompanyTotal = this.existingCompanyPlans
            .filter((p: any) => p.paymentStatus === 'PENDING' || p.paymentStatus === 0)
            .reduce((sum: number, p: any) => sum + p.amountToPay, 0);

        // Inicializar planes de empresa con los existentes (solo pendientes)
        this.companyPaymentPlans = this.existingCompanyPlans
            .filter((p: any) => p.paymentStatus === 'PENDING' || p.paymentStatus === 0)
            .map((p: any) => ({
                dueDate: new Date(p.dueDate),
                amountToPay: p.amountToPay,
                installmentNumber: p.installmentNumber,
                observations: p.observations,
                generateLink: false
            }));

        this.calculateTotals();
    }

    loadParticipants(): void {
        this.participantService.getAll().subscribe({
            next: (results: any) => {
                const participantsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                // Filtrar participantes que ya están en la inscripción
                const existingParticipantIds = this.enrollmentDetails.participantEnrollments
                    ?.map((pe: any) => pe.enrollment?.participant?.id)
                    .filter((id: any) => id != null) || [];

                this.participants = participantsData.filter(
                    (p: any) => !existingParticipantIds.includes(p.id)
                );
            },
            error: (err: any) => {
                console.error('Error cargando participantes', err);
            }
        });
    }

    onSearchParticipant(event: any): void {
        const query = event.filter;

        // Obtener IDs existentes para filtrar
        const existingParticipantIds = this.enrollmentDetails.participantEnrollments
            ?.map((pe: any) => pe.enrollment?.participant?.id)
            .filter((id: any) => id != null) || [];

        if (!query || query.length < 3) {
            // Si hay menos de 3 caracteres, recargar datos iniciales
            this.participantService.getAll().subscribe({
                next: (results: any) => {
                    const participantsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                    this.participants = participantsData.filter(
                        (p: any) => !existingParticipantIds.includes(p.id)
                    );
                },
                error: (err: any) => console.error('Error loading participants:', err)
            });
            return;
        }

        this.participantService.getAllFiltered(query).subscribe({
            next: (results: any) => {
                const participantsData = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                this.participants = participantsData.filter(
                    (p: any) => !existingParticipantIds.includes(p.id)
                );
            },
            error: (err: any) => {
                console.error('Error searching participants:', err);
            }
        });
    }

    // === Gestión de participantes ===
    addParticipant(): void {
        this.businessParticipants.push({
            participantId: null,
            coverageType: CoverageType.PERCENTAGE,
            coverageValue: 50
        });
    }

    removeParticipant(index: number): void {
        this.businessParticipants.splice(index, 1);
        this.calculateTotals();
    }

    onParticipantChange(index: number): void {
        this.calculateTotals();
    }

    onCoverageTypeChange(index: number): void {
        const participant = this.businessParticipants[index];

        switch (participant.coverageType) {
            case CoverageType.FULL:
                participant.coverageValue = 100;
                break;
            case CoverageType.NONE:
                participant.coverageValue = 0;
                break;
            case CoverageType.PERCENTAGE:
                participant.coverageValue = 50;
                break;
            case CoverageType.FIXED:
                participant.coverageValue = Math.round(this.inscriptionAmount * 0.5);
                break;
        }

        this.calculateTotals();
    }

    onCoverageValueChange(index: number): void {
        this.calculateTotals();
    }

    calculateCoverageDistribution(participant: ParticipantEntry): { companyAmount: number; participantAmount: number } {
        let companyAmount = 0;

        switch (participant.coverageType) {
            case CoverageType.FULL:
                companyAmount = this.inscriptionAmount;
                break;
            case CoverageType.NONE:
                companyAmount = 0;
                break;
            case CoverageType.PERCENTAGE:
                const percent = participant.coverageValue || 0;
                companyAmount = Math.round(this.inscriptionAmount * (percent / 100));
                break;
            case CoverageType.FIXED:
                companyAmount = Math.min(participant.coverageValue || 0, this.inscriptionAmount);
                break;
        }

        return {
            companyAmount,
            participantAmount: this.inscriptionAmount - companyAmount
        };
    }

    calculateTotals(): void {
        let totalCompany = 0;
        let totalParticipant = 0;

        for (const p of this.businessParticipants) {
            if (p.participantId) {
                const dist = this.calculateCoverageDistribution(p);
                totalCompany += dist.companyAmount;
                totalParticipant += dist.participantAmount;
            }
        }

        this.totalNewParticipantsAmount = totalCompany;
        this.totalParticipantPayAmount = totalParticipant;

        // Recalcular planes de empresa
        this.recalculateCompanyPlans();
    }

    recalculateCompanyPlans(): void {
        // Si hay planes existentes, recalcular montos
        if (this.companyPaymentPlans.length > 0 && this.totalNewParticipantsAmount > 0) {
            // Calcular total actual de empresa
            const existingTotal = this.existingCompanyPlans
                .filter((p: any) => p.paymentStatus === 'PENDING' || p.paymentStatus === 0)
                .reduce((sum: number, p: any) => sum + p.amountToPay, 0);

            const newTotal = existingTotal + this.totalNewParticipantsAmount;

            // Distribuir proporcionalmente (2 decimales; la última cuota absorbe el residuo).
            let accumulated = 0;
            this.companyPaymentPlans.forEach((plan, index) => {
                const isLast = index === this.companyPaymentPlans.length - 1;
                const originalPlan = this.existingCompanyPlans.find(
                    (p: any) => p.installmentNumber === plan.installmentNumber
                );
                if (isLast) {
                    plan.amountToPay = this.roundToTwo(newTotal - accumulated);
                } else if (originalPlan && existingTotal > 0) {
                    const proportion = originalPlan.amountToPay / existingTotal;
                    plan.amountToPay = this.roundToTwo(newTotal * proportion);
                    accumulated = this.roundToTwo(accumulated + plan.amountToPay);
                } else {
                    accumulated = this.roundToTwo(accumulated + (plan.amountToPay || 0));
                }
            });
        }

        this.totalCompanyAmount = this.companyPaymentPlans.reduce((sum, p) => sum + p.amountToPay, 0);
    }

    // === Planes de pago de empresa ===
    addCompanyPlan(): void {
        const lastPlan = this.companyPaymentPlans[this.companyPaymentPlans.length - 1];
        const nextDueDate = lastPlan ? new Date(lastPlan.dueDate) : new Date();
        nextDueDate.setMonth(nextDueDate.getMonth() + 1);

        this.companyPaymentPlans.push({
            dueDate: nextDueDate,
            amountToPay: 0,
            installmentNumber: this.companyPaymentPlans.length + 1,
            observations: '',
            generateLink: false
        });
    }

    removeCompanyPlan(index: number): void {
        this.companyPaymentPlans.splice(index, 1);
        this.updateInstallmentNumbers();
        this.calculateTotals();
    }

    updateInstallmentNumbers(): void {
        this.companyPaymentPlans.forEach((plan, idx) => {
            plan.installmentNumber = idx + 1;
        });
    }

    onCompanyPlanAmountChange(): void {
        this.totalCompanyAmount = this.companyPaymentPlans.reduce((sum, p) => sum + p.amountToPay, 0);
    }

    // Métodos de control de distribución
    getCompanyAssignedAmount(): number {
        return this.companyPaymentPlans.reduce((sum, p) => sum + (p.amountToPay || 0), 0);
    }

    getCompanyRemainingAmount(): number {
        const targetAmount = this.existingCompanyTotal + this.totalNewParticipantsAmount;
        return targetAmount - this.getCompanyAssignedAmount();
    }

    getTargetCompanyAmount(): number {
        return this.existingCompanyTotal + this.totalNewParticipantsAmount;
    }

    private roundToTwo(num: number): number {
        return Math.round((num + Number.EPSILON) * 100) / 100;
    }

    distributeCompanyAmount(): void {
        if (this.companyPaymentPlans.length === 0) return;

        const targetAmount = this.getTargetCompanyAmount();
        const numberOfPlans = this.companyPaymentPlans.length;
        const per = this.roundToTwo(targetAmount / numberOfPlans);
        let accumulated = 0;

        this.companyPaymentPlans.forEach((plan, index) => {
            const isLast = index === numberOfPlans - 1;
            // La última cuota absorbe el residuo para que la suma sea exacta.
            plan.amountToPay = isLast ? this.roundToTwo(targetAmount - accumulated) : per;
            if (!isLast) accumulated = this.roundToTwo(accumulated + per);
        });

        this.onCompanyPlanAmountChange();
        this.toast.showSuccess('Montos distribuidos automáticamente');
    }

    // === Navegación de pasos ===
    canProceedToStep2(): boolean {
        return this.businessParticipants.length > 0 &&
            this.businessParticipants.every(p => p.participantId !== null);
    }

    goToStep(step: number): void {
        if (step === 2 && !this.canProceedToStep2()) {
            this.toast.showWarning('Debe agregar al menos un participante');
            return;
        }
        this.step = step;
    }

    // === Validación y envío ===
    isFormValid(): boolean {
        return this.businessParticipants.length > 0 &&
            this.businessParticipants.every(p => p.participantId !== null) &&
            this.companyPaymentPlans.length > 0 &&
            this.companyPaymentPlans.every(p => p.amountToPay > 0) &&
            this.inscriptionAmount > 0;
    }

    submit(): void {
        if (!this.isFormValid()) {
            this.toast.showWarning('Revise los datos del formulario');
            return;
        }

        // Mostrar modal de confirmación
        this.confirmationConfig = {
            title: 'Confirmar Inscripción',
            message: `¿Está seguro de agregar ${this.businessParticipants.length} participante(s) a esta inscripción corporativa?`,
            confirmText: 'Sí, agregar',
            cancelText: 'Cancelar',
            type: 'info'
        };
        this.showConfirmationModal = true;
    }

    onConfirmationModalConfirmed(): void {
        this.showConfirmationModal = false;
        this.executeSubmit();
    }

    onConfirmationModalCancelled(): void {
        this.showConfirmationModal = false;
    }

    onConfirmationModalClosed(): void {
        this.showConfirmationModal = false;
    }

    private executeSubmit(): void {
        this.loading.show();

        const participants: CorporateEnrollmentParticipant[] = this.businessParticipants.map(p => ({
            participantId: p.participantId!,
            coverageType: p.coverageType,
            companyCoveragePercent: p.coverageType === CoverageType.PERCENTAGE ? p.coverageValue ?? undefined : undefined,
            companyCoverageAmount: p.coverageType === CoverageType.FIXED ? p.coverageValue ?? undefined : undefined
        }));

        const request: AddParticipantsRequest = {
            participants,
            updatedCompanyPlans: this.companyPaymentPlans.length > 0 ? this.companyPaymentPlans : undefined
        };

        this.corporateService.addParticipants(this.agreementId, request).subscribe({
            next: (response) => {
                this.loading.hide();
                this.toast.showSuccess(`Se agregaron ${response.createdEnrollments} participante(s) exitosamente`);
                this.router.navigate(['/dashboard/enrollments/corporate/view-v2', this.agreementId]);
            },
            error: (err) => {
                this.loading.hide();
                this.toast.showError(err.message || 'Error al agregar participantes');
            }
        });
    }

    cancel(): void {
        this.router.navigate(['/dashboard/enrollments/corporate/view-v2', this.agreementId]);
    }

    // === Utilidades ===
    formatCurrency(value: number): string {
        return new Intl.NumberFormat('es-CO', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }

    getParticipantName(participantId: number | null): string {
        if (!participantId) return '';
        const participant = this.participants.find(p => p.id === participantId);
        return participant ? `${participant.firstName} ${participant.lastName}` : '';
    }

    getCoverageTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {
            [CoverageType.FULL]: '100% Empresa',
            [CoverageType.PERCENTAGE]: 'Porcentaje',
            [CoverageType.FIXED]: 'Monto Fijo',
            [CoverageType.NONE]: '100% Participante'
        };
        return labels[type] || type;
    }
}
